import React from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'highlight.js/styles/monokai-sublime.css';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { Modals } from '@components/Modal';

import SharedNote from '@pages/SharedNote';
import SnapshotNote from '@pages/SnapshotNote';

import { FailedUi } from '@components/FailedUi';
import Notes from '@pages/Notes';
import Page404 from '@pages/Page404';
import '@services/ThemeService';

import './scss/app.scss';
import './scss/modal.scss';

type UserData = { role: string; tokensLeft: number; picture?: string; name?: string; given_name?: string; family_name?: string; email: string; emailMd5: string; id: string };
declare global {
  interface Window {
    user?: UserData;
  }
}

@observer
class App extends React.Component {
  @observable UserData = window.user;
  @observable isLoading = true;

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }

  componentDidMount(): void {
    // Hide the HTML loading screen now that React has mounted
    const loader = document.querySelector('.ui-loader-container');
    if (loader) {
      loader.remove();
    }
    void this._checkUserLoggedIn();
  }

  render(){

    if (this.isLoading) {
      return null;
    }

    return (
      <ErrorBoundary FallbackComponent={FailedUi}>
        <ToastContainer position="top-right" autoClose={3000} />
        <BrowserRouter>
          <Modals />
          <ErrorBoundary FallbackComponent={FailedUi}>
            <React.Suspense fallback={<div>Loading...</div>}>
              <main>
                <Routes>
                  <Route path="/s/*" element={<SharedNote />} />
                  <Route path="/snapshot/:noteId/:snapshotId" element={<SnapshotNote />} />
                  <Route path="/" element={<Notes />} />
                  <Route path="/n" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<Page404 />} />
                </Routes>
              </main>
            </React.Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  private async _checkUserLoggedIn() {
    let isLoggedIn = !!window.user;
    if (!isLoggedIn) {
      const userDataResponse = await fetch('/auth/userinfo');
      if (userDataResponse.ok) {
        const userData: { isAuthenticated: boolean; user?: UserData; hasRefreshToken?: boolean } = await userDataResponse.json();
        if (userData.isAuthenticated && userData.user) {
          window.user = userData.user;
          this.UserData = userData.user;
          isLoggedIn = true;
        }

        if (!isLoggedIn && userData.hasRefreshToken) {
          await fetch('/auth/refresh', { method: 'POST', credentials: 'include' });
          const recheckResponse = await fetch('/auth/userinfo');
          if (recheckResponse.ok) {
            const recheckData: { isAuthenticated: boolean; user?: UserData } = await recheckResponse.json();
            if (recheckData.isAuthenticated && recheckData.user) {
              window.user = recheckData.user;
              this.UserData = recheckData.user;
              isLoggedIn = true;
            }
          }
        }
      }

      if (!isLoggedIn && !window.location.pathname.startsWith('/s/')){
        void this._redirectToLogin();
      }
    }
    runInAction(() => {
      this.isLoading = false;
    });
  }

  private async _redirectToLogin(){
    //show error message by throwing an unhandled rejection
    setTimeout(() => {
      throw new Error('User not authenticated')
    }, 1);
    // add 1s delay before redirect
    await new Promise(resolve => setTimeout(resolve, 1000));
    window.location.href = '/auth/login';
  }
};

export default App;